import { useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Medal,
  Swords,
  Target,
  TrendingUp,
  Users,
} from 'lucide-react';
import { AppShell } from '../components/layout/AppShell';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { ErrorState } from '../components/common/ErrorState';
import { PeopleBreakdownCard } from '../components/stats/PeopleBreakdownCard';
import { useCachedData } from '../hooks/useCachedData';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { api } from '../services/api';
import type { MyStatsData, MyStatsEvolutionPoint } from '../types';

/** A round is a group of four playing every pairing once — always 3 matches. */
const MATCHES_PER_ROUND = 3;

const MONTH_LABELS = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez',
];

function monthLabel(month: string): string {
  const index = parseInt(month.split('-')[1], 10) - 1;
  return MONTH_LABELS[index] ?? month;
}

function StatTile({
  icon,
  label,
  value,
  tint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tint: string;
}) {
  return (
    <Card className="!p-4">
      <div className="flex items-center gap-2.5">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${tint}`}>
          {icon}
        </div>
        <div className="min-w-0">
          <p className="text-xl font-bold text-white leading-tight">{value}</p>
          {/* 11px so "Aproveitamento" clears the icon on a 390px screen */}
          <p className="text-[11px] text-white/50 truncate">{label}</p>
        </div>
      </div>
    </Card>
  );
}

function EvolutionChart({ points }: { points: MyStatsEvolutionPoint[] }) {
  return (
    <Card className="!p-4">
      <h3 className="font-semibold text-white flex items-center gap-2 mb-3">
        <TrendingUp className="w-5 h-5 text-turquoise" />
        Aproveitamento por mês
      </h3>
      <div className="h-40 flex items-end justify-between gap-2 px-2">
        {points.map((point, index) => (
          <motion.div
            key={point.month}
            initial={{ height: 0 }}
            animate={{ height: `${Math.max(point.winRate, 5)}%` }}
            transition={{ duration: 0.5, delay: 0.2 + index * 0.05 }}
            className="flex-1 bg-gradient-to-t from-turquoise/50 to-turquoise rounded-t-md relative min-h-[24px]"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 + index * 0.05 }}
              className="absolute -top-5 left-1/2 -translate-x-1/2 text-[11px] font-medium text-white whitespace-nowrap"
            >
              {point.winRate}%
            </motion.div>
            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] text-white/50">
              {monthLabel(point.month)}
            </div>
          </motion.div>
        ))}
      </div>
      <div className="h-6" />
    </Card>
  );
}

/**
 * Estatísticas — the player's numbers across every ranking they play.
 *
 * Built entirely on first-party ranking matches; the LetzPlay-scraped history
 * that used to feed this page is dormant.
 */
export function StatsPage() {
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  const fetcher = useCallback(() => api.stats.me(), []);
  const { data, isLoading, error, didRefresh, refetch } = useCachedData<MyStatsData>({
    cacheKey: 'bt_my_stats',
    fetcher,
  });

  const totals = data?.totals;
  const hasPlayed = !!totals && totals.matchesPlayed > 0;

  return (
    <AppShell>
      <div className="space-y-4 max-w-lg mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-display text-2xl font-bold text-white">Estatísticas</h1>
          <p className="text-sm text-white/50 mt-1">
            Seus números somando todos os rankings
          </p>
        </motion.div>

        {isLoading && (
          <div className="grid grid-cols-2 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="glass-card rounded-2xl p-4 animate-pulse h-[76px]" />
            ))}
          </div>
        )}

        {error && !isLoading && (
          <ErrorState message={error} isOffline={!isOnline} onRetry={refetch} />
        )}

        {!isLoading && !error && !hasPlayed && (
          <Card delay={0.1}>
            <div className="text-center py-2">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white/5 flex items-center justify-center">
                <BarChart3 className="w-8 h-8 text-white/30" />
              </div>
              <h3 className="font-semibold text-white mb-2">Nada por aqui ainda</h3>
              <p className="text-sm text-white/50 mb-5">
                Suas estatísticas aparecem depois da sua primeira partida em um
                ranking.
              </p>
              <Button onClick={() => navigate('/')} fullWidth>
                Ir para o início
              </Button>
            </div>
          </Card>
        )}

        {!isLoading && hasPlayed && totals && (
          <div className={didRefresh ? 'space-y-4 animate-data-refresh rounded-xl' : 'space-y-4'}>
            {/* Totals */}
            <div className="grid grid-cols-2 gap-3">
              <StatTile
                icon={<Swords className="w-5 h-5 text-coral" />}
                tint="bg-coral/20"
                label={`${totals.matchesWon}V · ${totals.matchesLost}D`}
                value={String(totals.matchesPlayed)}
              />
              <StatTile
                icon={<Target className="w-5 h-5 text-turquoise" />}
                tint="bg-turquoise/20"
                label="% vitórias"
                value={`${Math.round(totals.winRate)}%`}
              />
            </div>

            {/* Rounds, with how each one went. A round is a group of four
                playing three matches, so a result is always 3 to 0 wins. */}
            <Card className="!p-4">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-sunny/20 flex items-center justify-center shrink-0">
                  <Medal className="w-5 h-5 text-sunny" />
                </div>
                <div className="min-w-0">
                  <p className="text-xl font-bold text-white leading-tight">
                    {totals.roundsPlayed}
                  </p>
                  <p className="text-xs text-white/50">Rodadas jogadas</p>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2 mt-3 pt-3 border-t border-white/5">
                {totals.roundsByWins.map(({ wins, rounds }) => (
                  <div key={wins} className="text-center">
                    <p
                      className={`text-lg font-bold leading-tight ${rounds === 0
                          ? 'text-white/25'
                          : wins === 3
                            ? 'text-success-green'
                            : wins === 0
                              ? 'text-coral'
                              : 'text-white'
                        }`}
                    >
                      {rounds}
                    </p>
                    <p className="text-[11px] text-white/40 leading-tight mt-0.5">
                      {wins} {wins === 1 ? 'vitória' : 'vitórias'}
                    </p>
                    <p className="text-[10px] text-white/25 leading-tight">
                      {MATCHES_PER_ROUND - wins}{' '}
                      {MATCHES_PER_ROUND - wins === 1 ? 'derrota' : 'derrotas'}
                    </p>
                  </div>
                ))}
              </div>
            </Card>

            {data.evolution.length > 1 && <EvolutionChart points={data.evolution} />}

            {/* Partners */}
            {data.partners.length > 0 && (
              <PeopleBreakdownCard
                title="Suas parcerias"
                subtitle="Com quem você mais jogou"
                columnLabel="Parceiro"
                icon={<Users className="w-5 h-5 text-coral" />}
                iconTint="bg-coral/20"
                people={data.partners}
              />
            )}

            {/* Opponents */}
            {data.opponents.length > 0 && (
              <PeopleBreakdownCard
                title="Seus adversários"
                subtitle="Histórico contra oponentes"
                columnLabel="Adversário"
                icon={<Swords className="w-5 h-5 text-turquoise" />}
                iconTint="bg-turquoise/20"
                people={data.opponents}
              />
            )}

          </div>
        )}
      </div>
    </AppShell>
  );
}
